import { cloneDeep } from "lodash";
import { AnalysisMethod, AnalysisSet, DataSubset, GroupingFactor } from "../ars_ldm";
import { CURRENT_HIERARCHY_ID } from "./interface";

/* Helper type for these functions */
export type ArsObject = AnalysisSet | DataSubset | AnalysisMethod | GroupingFactor;

/**
 * Checks whether a collection contains an ARS object with the same name as the supplied item.
 *
 * @param items - Current hierarchy ARS objects to check.
 * @param item - Parent ARS object being compared.
 * @returns True when a current hierarchy object has the same name.
 */
export const hasArsObjectWithName = <T extends Pick<ArsObject, "name">>(
  items: T[] | undefined,
  item: Pick<ArsObject, "name">,
): boolean => (items ?? []).some((currentItem) => currentItem.name === item.name);

/**
 * Creates a current-level copy of an inherited ARS object while preserving referenced IDs.
 *
 * @param item - Inherited ARS object to copy into the current hierarchy.
 * @returns Copied ARS object marked as current-level metadata.
 */
export const cloneArsObject = <T extends ArsObject>(item: T): T => {
  const copiedItem: T = cloneDeep(item);
  copiedItem.id = crypto.randomUUID();
  copiedItem.fromHierarchyId = CURRENT_HIERARCHY_ID;
  delete copiedItem.disableEditing;
  return copiedItem;
};
